import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

import { Dialog, DialogContent } from '@/components/atoms/Dialog';
import { Button } from '@/components/atoms/NewPcButton';
import { ROUTES } from '@/routes';

import { useMembershipPreviewMode } from './MembershipContentLock';

export type MembershipOrderPreviewTutorialAction =
  | 'collectOrders'
  | 'confirmOrder'
  | 'cancelOrder'
  | 'setInvoice'
  | 'confirmCancel'
  | 'declineCancel';

type TutorialCopy = {
  title: string;
  description: string;
  result: string;
};

type MembershipOrderPreviewTutorialContextValue = {
  openTutorial: (action: MembershipOrderPreviewTutorialAction, orderCount?: number) => boolean;
};

const tutorialCopyByAction = {
  collectOrders: {
    title: '주문수집 체험',
    description: '연동된 마켓의 새 주문을 가져와 주문내역을 최신 상태로 맞추는 흐름이에요.',
    result: '구독 후에는 실제 마켓 주문을 수집하고 상태별 주문 수량을 갱신할 수 있어요.',
  },
  confirmOrder: {
    title: '주문 확인 체험',
    description: '선택한 신규 주문을 주문 확인 상태로 넘기는 업무 흐름이에요.',
    result: '구독 후에는 구매자에게 상품 준비중 또는 배송 대기중 상태로 반영돼요.',
  },
  cancelOrder: {
    title: '판매자 주문취소 체험',
    description: '판매자가 직접 결제 완료 주문을 취소해야 할 때 쓰는 흐름이에요.',
    result: '구독 후에는 마켓 정책에 맞춰 실제 주문취소 요청을 보낼 수 있어요.',
  },
  setInvoice: {
    title: '발송 처리 체험',
    description: '배송사와 운송장 번호를 입력한 주문을 발송 처리하는 흐름이에요.',
    result: '구독 후에는 선택한 주문의 운송장 정보를 각 마켓에 전송할 수 있어요.',
  },
  confirmCancel: {
    title: '취소 승인 체험',
    description: '구매자가 요청한 취소를 승인해 주문 상태를 정리하는 흐름이에요.',
    result: '구독 후에는 선택한 취소요청 주문을 실제로 승인 처리할 수 있어요.',
  },
  declineCancel: {
    title: '취소 거절 체험',
    description: '이미 발송 가능한 주문의 취소 요청을 거절하고 발송 정보로 처리하는 흐름이에요.',
    result: '구독 후에는 취소 거절과 함께 배송 정보를 실제 마켓에 반영할 수 있어요.',
  },
} satisfies Record<MembershipOrderPreviewTutorialAction, TutorialCopy>;

const MembershipOrderPreviewTutorialContext =
  createContext<MembershipOrderPreviewTutorialContextValue | null>(null);

export const MembershipOrderPreviewTutorialProvider = ({ children }: PropsWithChildren) => {
  const isPreviewMode = useMembershipPreviewMode();
  const navigate = useNavigate();
  const [isTutorialOpen, setIsTutorialOpen] = useState(false);
  const [activeAction, setActiveAction] = useState<MembershipOrderPreviewTutorialAction | null>(
    null,
  );
  const [orderCount, setOrderCount] = useState(0);

  const closeTutorial = () => setIsTutorialOpen(false);

  const openTutorial = useCallback(
    (action: MembershipOrderPreviewTutorialAction, nextOrderCount = 0) => {
      if (!isPreviewMode) return false;

      setActiveAction(action);
      setOrderCount(nextOrderCount);
      setIsTutorialOpen(true);
      return true;
    },
    [isPreviewMode],
  );

  const contextValue = useMemo(() => ({ openTutorial }), [openTutorial]);
  const activeCopy = activeAction ? tutorialCopyByAction[activeAction] : null;
  const orderCountText =
    orderCount > 0 ? `${orderCount.toLocaleString()}건 선택됨` : '실시간 주문수집';

  return (
    <MembershipOrderPreviewTutorialContext.Provider value={contextValue}>
      {children}
      <Dialog open={isTutorialOpen} onOpenChange={(open) => !open && closeTutorial()}>
        <DialogContent
          noCloseBtn
          className="w-[500px] max-w-[calc(100vw-32px)] p-[30px]"
          data-membership-preview-interactive="true"
        >
          {activeCopy && (
            <div className="flex flex-col gap-5">
              <div className="flex flex-col gap-1">
                <span className="text-point-500 Caption1M12">{orderCountText}</span>
                <div className="text-gr-900 Title3S18">{activeCopy.title}</div>
                <div className="text-gr-700 Body5M14">{activeCopy.description}</div>
              </div>
              <div className="rounded-[8px] bg-gr-50 p-4 text-gr-800 Body5M14">
                {activeCopy.result}
              </div>
              <div className="flex gap-3">
                <Button className="h-[44px] flex-1" variant="secondary" onClick={closeTutorial}>
                  계속 둘러보기
                </Button>
                <Button
                  className="h-[44px] flex-1"
                  variant="solid"
                  onClick={() => navigate(ROUTES.moreMembership)}
                >
                  구독하고 사용하기
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </MembershipOrderPreviewTutorialContext.Provider>
  );
};

export const useMembershipOrderPreviewTutorial = () => {
  const context = useContext(MembershipOrderPreviewTutorialContext);
  if (!context) {
    throw new Error(
      'useMembershipOrderPreviewTutorial must be used within MembershipOrderPreviewTutorialProvider',
    );
  }

  return context;
};
